import type { Translate } from './tileViewModel'

/**
 * B40 — the golfer's thoughts, in words.
 *
 * A25 — THE EXTRACTOR IS LITERAL-ONLY, so every line is written out in full as
 * the first argument of a `t()` call. That is the one thing a data table cannot
 * carry, and it is why the pool is split in two: `golferThought.BIRDIE_THOUGHTS`
 * holds the ids and the moments (which is what the logic reads), and this switch
 * holds the words (which is what the extractor reads).
 *
 * ADDING A LINE: a row in that table, a branch here, and the line in all sixteen
 * locales in `scripts/birdieLocales.mjs`. Nothing else changes.
 *
 * ON THE WORDS THEMSELVES. Every one is an INTERJECTION — one to three words, a
 * reaction rather than a joke. That is deliberate and it is what makes sixteen
 * locales tractable: a pun has to be re-invented in every language and a
 * reaction does not, because every language already has its own natural thing to
 * say when a ball drops or lips out. So the locale files carry what a golfer in
 * that language would actually mutter, not a translation of the English.
 *
 * Nothing here comments on the PLAYER — not their money, not whether they won.
 * It is his round, not theirs, and a bubble that celebrated or mourned a stake
 * would be the board talking about a bet while a putt was in the air.
 *
 * `en-soc` takes the English verbatim: not one of these says bet, stake, odds or
 * win, so the sweepstakes vocabulary has nothing to replace.
 */
export function golferThoughtCopy(id: string, t: Translate): string | undefined {
  switch (id) {
    // ── Before the ball. These know nothing about what is coming, and the
    //    pool they are drawn from cannot — see `golferThought`'s header.
    case 'steady':
      return t('Steady.')
    case 'rightThen':
      return t('Right then.')

    // ── The first one drops.
    case 'yes':
      return t('Yes.')
    case 'thatsIt':
      return t('That’s it.')

    // ── The second: building.
    case 'two':
      return t('Two.')
    case 'rollingNow':
      return t('Rolling now.')

    // ── All three home.
    case 'allThree':
      return t('All three!')
    case 'getIn':
      return t('Get in!')

    // ── A miss. Flat, and never anyone's fault.
    case 'ohh':
      return t('Ohh.')
    case 'justShort':
      return t('Just short.')
    case 'notThisTime':
      return t('Not this time.')

    // ── Two in and the third lips out.
    case 'soClose':
      return t('So close.')
    case 'nearly':
      return t('Nearly.')

    // ── The round's weather, and ONLY once it is public. See
    //    `golferThought`'s `THE ROUND TYPE IS A BETTABLE MARKET`.
    case 'onFire':
      return t('On fire.')
    case 'feelHeat':
      return t('Feel that heat.')
    case 'gettingCold':
      return t('Getting cold.')
    case 'iceOut':
      return t('Ice out here.')
    case 'plainDay':
      return t('A plain day.')
    case 'justGolf':
      return t('Just golf today.')

    default:
      return undefined
  }
}
